import Vue from 'vue'

const GLOBAL_KEY = '__HAPPILY_PERF_DEBUG__'
const ENABLE_KEYS = ['perfDebug', 'performanceDebug']
const ENABLE_VALUES = new Set(['1', 'true', 't', 'yes', 'on'])
const PANEL_STORAGE_KEY = 'perfDebugPanelExpanded'
const MAX_RECENT_LOGS = 12
const TRACKED_DOM_EVENTS = new Set([
  'resize',
  'scroll',
  'mousemove',
  'mouseup',
  'keydown',
  'click',
  'visibilitychange',
  'focus',
  'blur'
])

type AnyFunction = (...args: any[]) => any

interface PerfDebugLogEntry {
  id: number
  time: string
  scope: string
  message: string
}

interface BaseMeta {
  id: number
  route: string
  createdAt: number
  stack: string
}

interface FirebaseListenerMeta extends BaseMeta {
  target: string
}

interface DomListenerMeta extends BaseMeta {
  target: string
  eventType: string
  handlerName: string
  handlerRef: any
}

interface RootEventMeta extends BaseMeta {
  eventName: string
  handlerName: string
  handlerRef: any
}

interface RouteMountMeta {
  uid: number
  component: string
  route: string
  createdAt: number
}

interface PerfDebugState {
  nextId: number
  activeFirebase: Map<number, FirebaseListenerMeta>
  activeDom: Map<number, DomListenerMeta>
  activeRoot: Map<number, RootEventMeta>
  activeRoutes: Map<number, RouteMountMeta>
}

interface PerfDebugApi {
  enabled: boolean
  state: PerfDebugState
  recentLogs: PerfDebugLogEntry[]
  subscribe: (listener: () => void) => () => void
  log: (scope: string, message: string, details?: any) => void
  summary: () => {
    route: string
    firebase: Array<Record<string, any>>
    dom: Array<Record<string, any>>
    root: Array<Record<string, any>>
    routes: Array<Record<string, any>>
  }
  printSummary: () => any
}

interface PerfDebugMetric {
  wrapper: HTMLDivElement
  label: HTMLDivElement
  value: HTMLDivElement
}

declare global {
  interface Window {
    __HAPPILY_PERF_DEBUG__?: PerfDebugApi
  }
}

function normalizeFlag (value: string | null): string {
  return (value || '').trim().toLowerCase()
}

function isLocalPerfDebugHost (): boolean {
  if (typeof window === 'undefined') {
    return false
  }

  const hostname = window.location.hostname.toLowerCase()
  return (
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname === '[::1]' ||
    hostname.endsWith('.local')
  )
}

function canLoadPerfDebug (): boolean {
  return typeof window !== 'undefined' && (
    process.env.NODE_ENV !== 'production' ||
    isLocalPerfDebugHost()
  )
}

function isPerfDebugEnabled (): boolean {
  if (!canLoadPerfDebug()) {
    return false
  }

  try {
    return ENABLE_KEYS.some((key) => ENABLE_VALUES.has(normalizeFlag(window.localStorage.getItem(key))))
  } catch (error) {
    return false
  }
}

function nowLabel (): string {
  return new Date().toISOString().slice(11, 23)
}

function currentRouteLabel (router: any): string {
  const route = router && router.currentRoute
  if (!route) {
    return '[no-route]'
  }

  return route.fullPath || route.path || route.name || '[unknown-route]'
}

function functionName (handler: any): string {
  if (!handler) {
    return '[missing-handler]'
  }

  if (typeof handler === 'function' && handler.name) {
    return handler.name
  }

  if (handler.handleEvent && typeof handler.handleEvent === 'function') {
    return handler.handleEvent.name || '[event-object]'
  }

  return '[anonymous]'
}

function captureStack (): string {
  try {
    return (new Error().stack || '')
      .split('\n')
      .slice(3, 8)
      .join('\n')
  } catch (error) {
    return ''
  }
}

function durationMs (createdAt: number): number {
  return Date.now() - createdAt
}

function describeFirestoreTarget (ref: any): string {
  try {
    if (typeof ref.path === 'string') {
      return ref.path
    }
  } catch (error) {
  }

  try {
    const queryPath = ref && ref._query && ref._query.path
    if (queryPath && typeof queryPath.canonicalString === 'function') {
      return queryPath.canonicalString()
    }
  } catch (error) {
  }

  try {
    const internalPath = ref && ref._path
    if (internalPath && typeof internalPath.canonicalString === 'function') {
      return internalPath.canonicalString()
    }
  } catch (error) {
  }

  return '[unknown-firestore-target]'
}

function listRouteComponentMatch (vm: any): boolean {
  return Boolean(vm && vm.$vnode && vm.$vnode.data && vm.$vnode.data.routerView)
}

function componentName (vm: any): string {
  return vm.$options.name || vm.$options._componentTag || vm.$options.__file || '[anonymous-component]'
}

function matchingIds<T extends { handlerRef?: any, eventName?: string, eventType?: string, target?: string }>(
  map: Map<number, T>,
  predicate: (meta: T) => boolean
): number[] {
  return Array.from(map.entries())
    .filter(([, meta]) => predicate(meta))
    .map(([id]) => id)
}

function severityColor (value: number): string {
  if (value >= 6) {
    return '#ef4444'
  }

  if (value >= 1) {
    return '#f59e0b'
  }

  return '#d1d5db'
}

function formatDuration (ms: number): string {
  if (ms < 1000) {
    return `${ms}ms`
  }

  const seconds = Math.floor(ms / 1000)
  if (seconds < 60) {
    return `${seconds}s`
  }

  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) {
    return `${minutes}m ${seconds % 60}s`
  }

  const hours = Math.floor(minutes / 60)
  return `${hours}h ${minutes % 60}m`
}

function truncateText (value: string, maxLength: number): string {
  if (value.length <= maxLength) {
    return value
  }

  return `${value.slice(0, maxLength - 1)}...`
}

function setIgnoreHandler (handler: AnyFunction) {
  ;(handler as any).__happilyPerfIgnore = true
  return handler
}

function storageValue (key: string): string | null {
  try {
    return window.localStorage.getItem(key)
  } catch (error) {
    return null
  }
}

function setStorageValue (key: string, value: string) {
  try {
    window.localStorage.setItem(key, value)
  } catch (error) {
  }
}

function removeStorageValue (key: string) {
  try {
    window.localStorage.removeItem(key)
  } catch (error) {
  }
}

function isPanelExpanded (): boolean {
  return ENABLE_VALUES.has(normalizeFlag(storageValue(PANEL_STORAGE_KEY)))
}

function setPanelExpanded (expanded: boolean) {
  if (expanded) {
    setStorageValue(PANEL_STORAGE_KEY, 'true')
    return
  }

  removeStorageValue(PANEL_STORAGE_KEY)
}

function createMetricCard (labelText: string): PerfDebugMetric {
  const wrapper = document.createElement('div')
  Object.assign(wrapper.style, {
    background: 'rgba(15, 23, 42, 0.85)',
    border: '1px solid rgba(148, 163, 184, 0.18)',
    borderRadius: '10px',
    padding: '8px 10px',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px'
  })

  const label = document.createElement('div')
  label.textContent = labelText
  Object.assign(label.style, {
    fontSize: '11px',
    color: '#94a3b8',
    textTransform: 'uppercase',
    letterSpacing: '0.08em'
  })

  const value = document.createElement('div')
  value.textContent = '0'
  Object.assign(value.style, {
    fontSize: '20px',
    fontWeight: '700',
    color: '#d1d5db'
  })

  wrapper.appendChild(label)
  wrapper.appendChild(value)

  return { wrapper, label, value }
}

function createSectionHeading (labelText: string): HTMLDivElement {
  const heading = document.createElement('div')
  heading.textContent = labelText
  Object.assign(heading.style, {
    fontSize: '11px',
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    color: '#94a3b8',
    marginBottom: '6px'
  })
  return heading
}

function createPanelButton (labelText: string): HTMLButtonElement {
  const button = document.createElement('button')
  button.type = 'button'
  button.textContent = labelText
  Object.assign(button.style, {
    appearance: 'none',
    border: '1px solid rgba(148, 163, 184, 0.25)',
    background: 'rgba(15, 23, 42, 0.95)',
    color: '#f8fafc',
    borderRadius: '999px',
    padding: '7px 10px',
    fontSize: '11px',
    fontWeight: '700',
    cursor: 'pointer'
  })
  return button
}

function populateList (
  container: HTMLElement,
  items: string[],
  emptyText: string
) {
  while (container.firstChild) {
    container.removeChild(container.firstChild)
  }

  if (!items.length) {
    const empty = document.createElement('div')
    empty.textContent = emptyText
    Object.assign(empty.style, {
      fontSize: '12px',
      color: '#64748b'
    })
    container.appendChild(empty)
    return
  }

  items.forEach((text) => {
    const item = document.createElement('div')
    item.textContent = text
    item.title = text
    Object.assign(item.style, {
      fontSize: '12px',
      lineHeight: '1.4',
      color: '#e2e8f0',
      padding: '4px 0',
      borderTop: '1px solid rgba(148, 163, 184, 0.08)'
    })
    container.appendChild(item)
  })
}

async function copyTextToClipboard (text: string): Promise<boolean> {
  try {
    if (navigator && navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
      await navigator.clipboard.writeText(text)
      return true
    }
  } catch (error) {
  }

  try {
    const textarea = document.createElement('textarea')
    textarea.value = text
    textarea.setAttribute('readonly', 'true')
    Object.assign(textarea.style, {
      position: 'fixed',
      top: '-9999px',
      left: '-9999px',
      opacity: '0'
    })
    document.body.appendChild(textarea)
    textarea.select()
    const success = document.execCommand('copy')
    document.body.removeChild(textarea)
    return success
  } catch (error) {
    return false
  }
}

function createApi (router: any): PerfDebugApi {
  const state: PerfDebugState = {
    nextId: 0,
    activeFirebase: new Map(),
    activeDom: new Map(),
    activeRoot: new Map(),
    activeRoutes: new Map()
  }
  const recentLogs: PerfDebugLogEntry[] = []
  const listeners = new Set<() => void>()
  let logId = 0

  const notify = () => {
    listeners.forEach((listener) => {
      try {
        listener()
      } catch (error) {
      }
    })
  }

  const api: PerfDebugApi = {
    enabled: true,
    state,
    recentLogs,
    subscribe (listener: () => void) {
      listeners.add(listener)
      return () => {
        listeners.delete(listener)
      }
    },
    log (scope: string, message: string, details?: any) {
      const prefix = `[perf][${nowLabel()}][${scope}]`
      recentLogs.unshift({
        id: ++logId,
        time: nowLabel(),
        scope,
        message
      })

      if (recentLogs.length > MAX_RECENT_LOGS) {
        recentLogs.splice(MAX_RECENT_LOGS)
      }

      if (typeof details === 'undefined') {
        console.log(prefix, message)
        notify()
        return
      }

      console.log(prefix, message, details)
      notify()
    },
    summary () {
      return {
        route: currentRouteLabel(router),
        firebase: Array.from(state.activeFirebase.values())
          .sort((a, b) => a.createdAt - b.createdAt)
          .map((meta) => ({
          id: meta.id,
          target: meta.target,
          route: meta.route,
          aliveMs: durationMs(meta.createdAt),
          stack: meta.stack
        })),
        dom: Array.from(state.activeDom.values())
          .sort((a, b) => a.createdAt - b.createdAt)
          .map((meta) => ({
          id: meta.id,
          target: meta.target,
          eventType: meta.eventType,
          handlerName: meta.handlerName,
          route: meta.route,
          aliveMs: durationMs(meta.createdAt),
          stack: meta.stack
        })),
        root: Array.from(state.activeRoot.values())
          .sort((a, b) => a.createdAt - b.createdAt)
          .map((meta) => ({
          id: meta.id,
          eventName: meta.eventName,
          handlerName: meta.handlerName,
          route: meta.route,
          aliveMs: durationMs(meta.createdAt),
          stack: meta.stack
        })),
        routes: Array.from(state.activeRoutes.values())
          .sort((a, b) => a.createdAt - b.createdAt)
          .map((meta) => ({
          uid: meta.uid,
          component: meta.component,
          route: meta.route,
          aliveMs: durationMs(meta.createdAt)
        }))
      }
    },
    printSummary () {
      const summary = api.summary()
      console.group('[perf] Active listener summary')
      console.log('route', summary.route)
      console.table(summary.firebase)
      console.table(summary.dom)
      console.table(summary.root)
      console.table(summary.routes)
      console.groupEnd()
      return summary
    }
  }

  return api
}

function attachDebugPanel (api: PerfDebugApi, router: any) {
  const root = document.createElement('div')
  root.id = 'happily-perf-debug-panel'
  Object.assign(root.style, {
    position: 'fixed',
    right: '16px',
    bottom: '16px',
    zIndex: '2147483647',
    pointerEvents: 'none',
    fontFamily: '"SFMono-Regular", Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace'
  })

  const badge = document.createElement('button')
  badge.type = 'button'
  Object.assign(badge.style, {
    pointerEvents: 'auto',
    appearance: 'none',
    border: '1px solid rgba(251, 191, 36, 0.4)',
    background: 'rgba(15, 23, 42, 0.94)',
    color: '#f8fafc',
    borderRadius: '999px',
    padding: '10px 12px',
    minWidth: '150px',
    boxShadow: '0 14px 32px rgba(2, 6, 23, 0.35)',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: '700',
    textAlign: 'left'
  })

  const panel = document.createElement('div')
  Object.assign(panel.style, {
    pointerEvents: 'auto',
    width: '360px',
    maxHeight: '75vh',
    overflow: 'auto',
    marginTop: '10px',
    borderRadius: '16px',
    border: '1px solid rgba(148, 163, 184, 0.22)',
    background: 'rgba(2, 6, 23, 0.96)',
    color: '#f8fafc',
    boxShadow: '0 22px 50px rgba(2, 6, 23, 0.5)',
    backdropFilter: 'blur(10px)',
    padding: '14px'
  })

  const title = document.createElement('div')
  title.textContent = 'Perf Debug'
  Object.assign(title.style, {
    fontSize: '15px',
    fontWeight: '700',
    marginBottom: '4px'
  })

  const subTitle = document.createElement('div')
  subTitle.textContent = 'Tracks listeners and route activity'
  Object.assign(subTitle.style, {
    fontSize: '12px',
    color: '#94a3b8',
    marginBottom: '12px'
  })

  const routeLine = document.createElement('div')
  Object.assign(routeLine.style, {
    fontSize: '12px',
    color: '#e2e8f0',
    padding: '8px 10px',
    background: 'rgba(15, 23, 42, 0.8)',
    borderRadius: '10px',
    border: '1px solid rgba(148, 163, 184, 0.18)',
    marginBottom: '12px',
    wordBreak: 'break-word'
  })

  const countsGrid = document.createElement('div')
  Object.assign(countsGrid.style, {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: '8px',
    marginBottom: '12px'
  })

  const firebaseMetric = createMetricCard('Firebase')
  const domMetric = createMetricCard('DOM')
  const rootMetric = createMetricCard('Root')
  const routeMetric = createMetricCard('Views')
  countsGrid.appendChild(firebaseMetric.wrapper)
  countsGrid.appendChild(domMetric.wrapper)
  countsGrid.appendChild(rootMetric.wrapper)
  countsGrid.appendChild(routeMetric.wrapper)

  const actionRow = document.createElement('div')
  Object.assign(actionRow.style, {
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap',
    marginBottom: '12px'
  })

  const copyButton = createPanelButton('Copy summary')
  const consoleButton = createPanelButton('Console summary')
  const collapseButton = createPanelButton('Collapse')
  actionRow.appendChild(copyButton)
  actionRow.appendChild(consoleButton)
  actionRow.appendChild(collapseButton)

  const statusLine = document.createElement('div')
  Object.assign(statusLine.style, {
    minHeight: '16px',
    fontSize: '11px',
    color: '#94a3b8',
    marginBottom: '12px'
  })

  const activeHeading = createSectionHeading('Oldest Active Work')
  const activeList = document.createElement('div')
  Object.assign(activeList.style, {
    marginBottom: '12px',
    padding: '6px 10px',
    background: 'rgba(15, 23, 42, 0.72)',
    borderRadius: '10px',
    border: '1px solid rgba(148, 163, 184, 0.16)'
  })

  const logHeading = createSectionHeading('Recent Events')
  const logList = document.createElement('div')
  Object.assign(logList.style, {
    padding: '6px 10px',
    background: 'rgba(15, 23, 42, 0.72)',
    borderRadius: '10px',
    border: '1px solid rgba(148, 163, 184, 0.16)'
  })

  panel.appendChild(title)
  panel.appendChild(subTitle)
  panel.appendChild(routeLine)
  panel.appendChild(countsGrid)
  panel.appendChild(actionRow)
  panel.appendChild(statusLine)
  panel.appendChild(activeHeading)
  panel.appendChild(activeList)
  panel.appendChild(logHeading)
  panel.appendChild(logList)

  root.appendChild(badge)
  root.appendChild(panel)
  document.body.appendChild(root)

  let expanded = isPanelExpanded()
  let statusTimer: number | null = null

  const setStatus = (message: string, color: string) => {
    statusLine.textContent = message
    statusLine.style.color = color
    if (statusTimer) {
      window.clearTimeout(statusTimer)
    }

    statusTimer = window.setTimeout(() => {
      statusLine.textContent = ''
      statusLine.style.color = '#94a3b8'
      statusTimer = null
    }, 2400)
  }

  const render = () => {
    const summary = api.summary()
    const firebaseCount = summary.firebase.length
    const domCount = summary.dom.length
    const rootCount = summary.root.length
    const routeCount = summary.routes.length

    badge.textContent = `PERF  F:${firebaseCount}  D:${domCount}  R:${rootCount}  V:${routeCount}${expanded ? '  Hide' : ''}`
    badge.style.borderColor = firebaseCount + domCount + rootCount > 0
      ? 'rgba(251, 191, 36, 0.5)'
      : 'rgba(148, 163, 184, 0.25)'

    panel.style.display = expanded ? 'block' : 'none'
    routeLine.textContent = `Route: ${summary.route}`

    firebaseMetric.value.textContent = `${firebaseCount}`
    firebaseMetric.value.style.color = severityColor(firebaseCount)
    domMetric.value.textContent = `${domCount}`
    domMetric.value.style.color = severityColor(domCount)
    rootMetric.value.textContent = `${rootCount}`
    rootMetric.value.style.color = severityColor(rootCount)
    routeMetric.value.textContent = `${routeCount}`
    routeMetric.value.style.color = routeCount > 1 ? severityColor(routeCount - 1) : '#d1d5db'

    const activeItems: string[] = []
    if (summary.firebase[0]) {
      activeItems.push(`Firebase: ${truncateText(summary.firebase[0].target, 48)} (${formatDuration(summary.firebase[0].aliveMs)})`)
    }
    if (summary.dom[0]) {
      activeItems.push(`DOM: ${summary.dom[0].target}.${summary.dom[0].eventType} ${summary.dom[0].handlerName} (${formatDuration(summary.dom[0].aliveMs)})`)
    }
    if (summary.root[0]) {
      activeItems.push(`Root: ${summary.root[0].eventName} ${summary.root[0].handlerName} (${formatDuration(summary.root[0].aliveMs)})`)
    }
    if (summary.routes[0]) {
      activeItems.push(`View: ${truncateText(summary.routes[0].component, 44)} (${formatDuration(summary.routes[0].aliveMs)})`)
    }
    populateList(activeList, activeItems, 'No active listeners or mounted route views.')

    const logItems = api.recentLogs.map((entry) => {
      return `${entry.time} [${entry.scope}] ${entry.message}`
    })
    populateList(logList, logItems, 'No debug events yet.')
  }

  badge.onclick = () => {
    expanded = !expanded
    setPanelExpanded(expanded)
    render()
  }

  collapseButton.onclick = () => {
    expanded = false
    setPanelExpanded(false)
    render()
  }

  consoleButton.onclick = setIgnoreHandler(() => {
    api.printSummary()
    setStatus('Summary printed to the console.', '#38bdf8')
  })

  copyButton.onclick = setIgnoreHandler(async () => {
    const payload = JSON.stringify(api.summary(), null, 2)
    const success = await copyTextToClipboard(payload)
    setStatus(
      success ? 'Summary copied to clipboard.' : 'Copy failed. Use Console summary instead.',
      success ? '#22c55e' : '#f59e0b'
    )
  })

  api.subscribe(render)
  render()
  api.log('panel', `widget ready on ${currentRouteLabel(router)}`)
}

function patchFirestoreListeners (api: PerfDebugApi, firebase: any, router: any) {
  if (!firebase || typeof firebase.firestore !== 'function') {
    return
  }

  const firestore = firebase.firestore()
  const targets = [
    Object.getPrototypeOf(firestore.collection('__perf_debug_probe__')),
    Object.getPrototypeOf(firestore.doc('__perf_debug_probe__/doc'))
  ].filter(Boolean)

  targets.forEach((proto: any) => {
    if (!proto || proto.__happilyPerfSnapshotPatched) {
      return
    }

    const originalOnSnapshot = proto.onSnapshot
    if (typeof originalOnSnapshot !== 'function') {
      return
    }

    proto.onSnapshot = function (...args: any[]) {
      const id = ++api.state.nextId
      const meta: FirebaseListenerMeta = {
        id,
        target: describeFirestoreTarget(this),
        route: currentRouteLabel(router),
        createdAt: Date.now(),
        stack: captureStack()
      }

      api.state.activeFirebase.set(id, meta)
      api.log('firebase', `subscribe #${id} ${meta.target}`, {
        route: meta.route,
        active: api.state.activeFirebase.size
      })

      const unsubscribe = originalOnSnapshot.apply(this, args)

      return (...unsubscribeArgs: any[]) => {
        const existing = api.state.activeFirebase.get(id)
        if (existing) {
          api.state.activeFirebase.delete(id)
          api.log('firebase', `unsubscribe #${id} ${existing.target}`, {
            active: api.state.activeFirebase.size,
            aliveMs: durationMs(existing.createdAt)
          })
        }

        if (typeof unsubscribe === 'function') {
          return unsubscribe.apply(this, unsubscribeArgs)
        }
      }
    }

    proto.__happilyPerfSnapshotPatched = true
  })
}

function patchDomListeners (api: PerfDebugApi, router: any) {
  const patchTarget = (target: any, targetName: string) => {
    if (!target || target.__happilyPerfEventPatched) {
      return
    }

    const originalAdd = target.addEventListener
    const originalRemove = target.removeEventListener

    target.addEventListener = function (eventType: string, handler: any, options?: any) {
      if (TRACKED_DOM_EVENTS.has(eventType) && handler && !handler.__happilyPerfIgnore) {
        const id = ++api.state.nextId
        const meta: DomListenerMeta = {
          id,
          target: targetName,
          eventType,
          handlerName: functionName(handler),
          handlerRef: handler,
          route: currentRouteLabel(router),
          createdAt: Date.now(),
          stack: captureStack()
        }

        api.state.activeDom.set(id, meta)
        api.log('dom', `add ${targetName}.${eventType} #${id}`, {
          handler: meta.handlerName,
          route: meta.route,
          active: api.state.activeDom.size
        })
      }

      return originalAdd.call(this, eventType, handler, options)
    }

    target.removeEventListener = function (eventType: string, handler: any, options?: any) {
      if (TRACKED_DOM_EVENTS.has(eventType) && handler && !handler.__happilyPerfIgnore) {
        const ids = matchingIds(api.state.activeDom, (meta) => {
          return meta.target === targetName && meta.eventType === eventType && meta.handlerRef === handler
        })

        ids.forEach((id) => {
          const meta = api.state.activeDom.get(id)
          if (!meta) {
            return
          }

          api.state.activeDom.delete(id)
          api.log('dom', `remove ${targetName}.${eventType} #${id}`, {
            active: api.state.activeDom.size,
            aliveMs: durationMs(meta.createdAt)
          })
        })
      }

      return originalRemove.call(this, eventType, handler, options)
    }

    target.__happilyPerfEventPatched = true
  }

  patchTarget(window, 'window')
  patchTarget(document, 'document')

  const visibilityHandler = () => {
    api.log('page', `visibility ${document.hidden ? 'hidden' : 'visible'}`, {
      route: currentRouteLabel(router),
      activeFirebase: api.state.activeFirebase.size,
      activeDom: api.state.activeDom.size,
      activeRoot: api.state.activeRoot.size
    })
  }

  ;(visibilityHandler as any).__happilyPerfIgnore = true
  document.addEventListener('visibilitychange', visibilityHandler)
}

function patchRootEvents (api: PerfDebugApi, router: any) {
  const proto = Vue.prototype as any
  if (proto.__happilyPerfRootPatched) {
    return
  }

  const originalOn = proto.$on
  const originalOff = proto.$off

  proto.$on = function (eventName: string | string[], handler: AnyFunction) {
    if (this && this.$root === this) {
      const names = Array.isArray(eventName) ? eventName : [eventName]
      names.forEach((name) => {
        const id = ++api.state.nextId
        const meta: RootEventMeta = {
          id,
          eventName: name,
          handlerName: functionName(handler),
          handlerRef: handler,
          route: currentRouteLabel(router),
          createdAt: Date.now(),
          stack: captureStack()
        }

        api.state.activeRoot.set(id, meta)
        api.log('root', `on ${name} #${id}`, {
          handler: meta.handlerName,
          route: meta.route,
          active: api.state.activeRoot.size
        })
      })
    }

    return originalOn.call(this, eventName, handler)
  }

  proto.$off = function (eventName?: string | string[], handler?: AnyFunction) {
    if (this && this.$root === this) {
      if (!eventName) {
        api.state.activeRoot.clear()
        api.log('root', 'off all root events', {
          active: 0
        })
      } else {
        const names = Array.isArray(eventName) ? eventName : [eventName]
        names.forEach((name) => {
          const ids = matchingIds(api.state.activeRoot, (meta) => {
            if (meta.eventName !== name) {
              return false
            }

            if (!handler) {
              return true
            }

            return meta.handlerRef === handler
          })

          ids.forEach((id) => {
            const meta = api.state.activeRoot.get(id)
            if (!meta) {
              return
            }

            api.state.activeRoot.delete(id)
            api.log('root', `off ${name} #${id}`, {
              active: api.state.activeRoot.size,
              aliveMs: durationMs(meta.createdAt)
            })
          })
        })
      }
    }

    return originalOff.call(this, eventName, handler)
  }

  proto.__happilyPerfRootPatched = true
}

function patchRouteLifecycle (api: PerfDebugApi, router: any) {
  Vue.mixin({
    mounted () {
      const vm = this as any

      if (!listRouteComponentMatch(vm)) {
        return
      }

      const meta: RouteMountMeta = {
        uid: vm._uid,
        component: componentName(vm),
        route: currentRouteLabel(router),
        createdAt: Date.now()
      }

      api.state.activeRoutes.set(meta.uid, meta)
      api.log('route-view', `mount ${meta.component} #${meta.uid}`, {
        route: meta.route,
        active: api.state.activeRoutes.size
      })
    },
    beforeDestroy () {
      const vm = this as any

      if (!listRouteComponentMatch(vm)) {
        return
      }

      const existing = api.state.activeRoutes.get(vm._uid)
      if (existing) {
        api.state.activeRoutes.delete(vm._uid)
        api.log('route-view', `unmount ${existing.component} #${existing.uid}`, {
          route: currentRouteLabel(router),
          active: api.state.activeRoutes.size,
          aliveMs: durationMs(existing.createdAt)
        })
        return
      }

      api.log('route-view', `unmount ${componentName(vm)} #${vm._uid}`, {
        route: currentRouteLabel(router),
        active: api.state.activeRoutes.size
      })
    }
  })
}

function patchRouterLifecycle (api: PerfDebugApi, router: any) {
  if (!router || router.__happilyPerfRouterPatched) {
    return
  }

  router.beforeEach((to: any, from: any, next: AnyFunction) => {
    api.log('route', `navigate ${from.fullPath || from.path || '[startup]'} -> ${to.fullPath || to.path || to.name}`, {
      from: from.name || null,
      to: to.name || null
    })
    next()
  })

  router.afterEach((to: any) => {
    api.log('route', `active ${to.fullPath || to.path || to.name}`, {
      activeFirebase: api.state.activeFirebase.size,
      activeDom: api.state.activeDom.size,
      activeRoot: api.state.activeRoot.size,
      activeRouteViews: api.state.activeRoutes.size
    })
  })

  router.__happilyPerfRouterPatched = true
}

export function setupPerformanceDebug (options: { firebase: any, router: any }) {
  if (typeof window === 'undefined' || window[GLOBAL_KEY]) {
    return
  }

  if (!canLoadPerfDebug()) {
    return
  }

  if (!isPerfDebugEnabled()) {
    return
  }

  const api = createApi(options.router)
  window[GLOBAL_KEY] = api

  patchFirestoreListeners(api, options.firebase, options.router)
  patchDomListeners(api, options.router)
  patchRootEvents(api, options.router)
  patchRouteLifecycle(api, options.router)
  patchRouterLifecycle(api, options.router)
  attachDebugPanel(api, options.router)

  api.log('setup', 'performance debug enabled', {
    howToDisable: 'localStorage.removeItem("perfDebug")'
  })
}
